import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { navigate, useLocation } from '@reach/router'
import { SaveEvent, ClickEvent } from './AnalyticsEvents'
import { ApiFetch, ApiPut } from './Api'
import type InAppNotification from '../../models/InAppNotification'
import type InAppNotificationResponse from '../../models/InAppNotificationResponse'
import UserContext, { Roles } from '../../UserContext'
import { displayNotificationTimeAgo } from '../../utilities'
import { ProviderCommunicationPreferencesEnum } from '../../models/enums/CommunicationPreferences'
import { getNotificationTargetPath } from '../Notifications/navigation'

type NotificationBellProps = {
    previewCount?: number
}

const MAX_BADGE_COUNT = 99

type UserCommunicationPreferences = {
    communicationMethods?: string[]
}

const NotificationBell: React.FC<NotificationBellProps> = ({ previewCount = 3 }) => {
    const userContext = useContext(UserContext)
    const { hasAnyRole } = userContext
    const { pathname } = useLocation()
    const [open, setOpen] = useState(false)
    const [loading, setLoading] = useState(false)
    const [isPreferenceLoading, setIsPreferenceLoading] = useState(true)
    const [isInAppEnabled, setIsInAppEnabled] = useState(false)
    const [unreadCount, setUnreadCount] = useState(0)
    const [notifications, setNotifications] = useState<InAppNotification[]>([])
    const bellRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const endpoint = hasAnyRole(Roles.Provider)
            ? 'User/ProviderPreferences'
            : hasAnyRole(Roles.WaiverServiceCoordinator, Roles.WaiverServiceLeader, Roles.MarketAdmin)
                ? 'User/CareManagerPreferences'
                : null

        if (!endpoint) {
            setIsInAppEnabled(false)
            setIsPreferenceLoading(false)
            return
        }

        setIsPreferenceLoading(true)
        ApiFetch<UserCommunicationPreferences>(endpoint)
            .then((result) => {
                const methods = result?.communicationMethods || []
                setIsInAppEnabled(methods.includes(ProviderCommunicationPreferencesEnum.InAppNotifications))
            })
            .catch(() => setIsInAppEnabled(false))
            .finally(() => setIsPreferenceLoading(false))
    }, [hasAnyRole])

    const loadNotifications = useCallback((take: number) => {
        setLoading(true)
        ApiFetch<InAppNotificationResponse>('Notification/InApp', { take })
            .then((result) => {
                setUnreadCount(result.unreadCount || 0)
                setNotifications(result.notifications || [])
            })
            // eslint-disable-next-line no-console
            .catch(console.log)
            .finally(() => setLoading(false))
    }, [])

    useEffect(() => {
        if (!isInAppEnabled) return
        loadNotifications(previewCount)
    }, [isInAppEnabled, loadNotifications, previewCount])

    useEffect(() => {
        const onDocumentClick = (event: MouseEvent) => {
            if (!bellRef.current) return
            if (event.target instanceof Node && bellRef.current.contains(event.target)) return
            setOpen(false)
        }

        document.addEventListener('mousedown', onDocumentClick)
        return () => document.removeEventListener('mousedown', onDocumentClick)
    }, [])

    const openOrClose = () => {
        if (!isInAppEnabled) return
        const shouldOpen = !open
        setOpen(shouldOpen)
        if (shouldOpen) {
            SaveEvent(ClickEvent(userContext, pathname, 'Notification Bell'))
            loadNotifications(previewCount)
        }
    }

    const markAsReadAndNavigate = (notification: InAppNotification) => {
        const goTo = getNotificationTargetPath(notification)

        const onNavigate = () => {
            setOpen(false)
            void navigate(goTo)
        }

        if (!notification.isRead) {
            ApiPut<boolean>(`Notification/${notification.notificationID}/Viewed`)
                .then(() => {
                    setNotifications((items) => items.map((item) => item.notificationID === notification.notificationID ? { ...item, isRead: true } : item))
                    setUnreadCount((count) => Math.max(0, count - 1))
                    onNavigate()
                })
                .catch(onNavigate)
            return
        }

        onNavigate()
    }

    const onViewAll = () => {
        SaveEvent(ClickEvent(userContext, pathname, 'View All Notifications'))
        setOpen(false)
        void navigate('/notifications')
    }

    const onClearAll = () => {
        SaveEvent(ClickEvent(userContext, pathname, 'Clear All Notifications'))
        ApiPut<boolean>('Notification/Viewed/All')
            .then(() => {
                setUnreadCount(0)
                setNotifications((items) => items.map((item) => ({ ...item, isRead: true })))
            })
            // eslint-disable-next-line no-console
            .catch(console.log)
    }

    if (isPreferenceLoading || !isInAppEnabled) return null

    return (
        <div className='notification-bell' ref={bellRef}>
            <button className='notification-bell-trigger' type='button' onClick={openOrClose} aria-label='Open notifications'>
                <i className='far fa-bell' aria-hidden='true'></i>
                {unreadCount > 0 && <span className='notification-bell-count'>{unreadCount > MAX_BADGE_COUNT ? `${MAX_BADGE_COUNT}+` : unreadCount}</span>}
            </button>

            {open && (
                <div className='notification-preview-dropdown'>
                    <div className='notification-preview-header'>
                        <h4>Notifications</h4>
                        <button type='button' className='notification-close' aria-label='Close notifications' onClick={() => setOpen(false)}>
                            <i className='far fa-times' aria-hidden='true'></i>
                        </button>
                    </div>
                    <ul className='notification-preview-list'>
                        {loading && <li className='notification-empty'>Loading...</li>}
                        {!loading && notifications.length === 0 && <li className='notification-empty'>No notifications</li>}
                        {!loading && notifications.map((notification) => (
                            <li key={notification.notificationID}>
                                <button type='button' onClick={() => markAsReadAndNavigate(notification)} className='notification-item'>
                                    <span className={`notification-indicator ${notification.isRead ? 'read' : 'unread'}`} aria-hidden='true'></span>
                                    <span className='notification-body'>
                                        <span className='notification-title'>{notification.title}</span>
                                        <span className='notification-time'>{displayNotificationTimeAgo(notification.createdOn)}</span>
                                    </span>
                                </button>
                            </li>
                        ))}
                    </ul>
                    {hasAnyRole(Roles.Provider) && unreadCount > 0 && (
                        <button type='button' className='notification-clear-all' onClick={onClearAll}>Clear All Notifications</button>
                    )}
                    <button type='button' className='notification-view-all' onClick={onViewAll}>View All Notifications<i className='far fa-angle-right' aria-hidden='true'></i></button>
                </div>
            )}
        </div>
    )
}

export default NotificationBell
